import {Alert, Button, Col, Drawer, Row, Space, Tooltip} from "antd";
import {StyledCustomersDataBox} from "../../../../styles/customers/customers";
import {CloseOutlined, QuestionCircleOutlined} from "@ant-design/icons";
import {Fragment, useState} from "react";
import {StyledRevenueListItem} from "./revenue-section.style";
import {usePricingCurrencies} from "@/features/currencies";
import {useDigitalProductSalesInsights} from "../../../../features/digital-products";
import {FullPageLoader} from "../../../full-page-loader";

type TRevenueSection = {
  productId: number;
  createdAt?: string;
};

export function RevenueSection({productId, createdAt}: TRevenueSection) {
  const [openRevenueBreakDown, setOpenRevenueBreakDown] = useState(false);
  const pricingCurrencies = usePricingCurrencies();
  const insightsQuery = useDigitalProductSalesInsights(productId, createdAt);

  const showRevenueBreakDown = () => setOpenRevenueBreakDown(true);
  const onCloseRevenueBreakDown = () => setOpenRevenueBreakDown(false);

  if (insightsQuery.isLoading) {
    return <FullPageLoader />;
  }
  if (insightsQuery.isError) {
    return (
      <Alert
        message="Error"
        description={insightsQuery.error?.message}
        type="error"
        showIcon
      />
    );
  }

  const revenueByCurrency = Object.entries(
    insightsQuery.data?.totalRevenueByCurrency ?? {}
  );
  const [headlineCurrency, headlineAmount] = revenueByCurrency[0] ?? [];

  return (
    <div style={{marginTop: "2.4rem"}}>
      <Space size={16} direction="vertical" style={{display: "flex"}}>
        <Row gutter={[59, 20]}>
          <Col span={24} md={12}>
            <StyledCustomersDataBox
              style={{
                backgroundColor: "#EAE6F3",
                color: "#272037",
                borderRadius: "5px",
                border: "1.6px solid #43375D",
              }}
            >
              <div style={{backgroundColor: "#B3A8CD"}} className="data-circle"></div>
              <p className="data-box-title">
                Total revenue
                <Tooltip
                  placement="left"
                  title="Your total revenue is calculated in your payout currency"
                >
                  <Button icon={<QuestionCircleOutlined />} size="small" type="text" />
                </Tooltip>
              </p>
              <h2 className="data-box-value">{`${
                headlineCurrency ?? pricingCurrencies.bankAccount?.currency
              } ${(headlineAmount ?? 0).toLocaleString()}`}</h2>
            </StyledCustomersDataBox>
          </Col>
          <Col span={24} md={12}>
            <StyledCustomersDataBox
              style={{
                backgroundColor: "#E7EDE1",
                color: "#16350F",
                borderRadius: "5px",
                border: "1.6px solid #355D2C",
              }}
            >
              <div style={{backgroundColor: "#B6C2A8"}} className="data-circle"></div>
              <p className="data-box-title">
                Total sales
                <Tooltip placement="left" title="Total unit of products sold">
                  <Button icon={<QuestionCircleOutlined />} size="small" type="text" />
                </Tooltip>
              </p>
              <h2 className="data-box-value">{insightsQuery.data?.totalSales ?? 0}</h2>
            </StyledCustomersDataBox>
          </Col>
        </Row>
        {revenueByCurrency.length > 0 && (
          <p style={{color: "#595959"}}>
            Check your earnings in all your currencies,{" "}
            <Button
              onClick={showRevenueBreakDown}
              style={{padding: "0"}}
              type="link"
              size="small"
            >
              click here
            </Button>{" "}
            to view
          </p>
        )}
      </Space>
      <Drawer
        title="Revenue breakdown"
        placement="right"
        onClose={onCloseRevenueBreakDown}
        visible={openRevenueBreakDown}
        closable={false}
        width={320}
        extra={
          <Button
            type="ghost"
            size="small"
            icon={<CloseOutlined />}
            onClick={onCloseRevenueBreakDown}
          ></Button>
        }
      >
        <Row gutter={[20, 16]}>
          {revenueByCurrency.map(([currency, amount]) => (
            <Fragment key={currency}>
              <Col span={24}>
                <StyledRevenueListItem>
                  <div className="item-volume">
                    <p className="item-title">Total volume</p>
                    <p className="item-value">{`${currency} ${amount.toLocaleString(
                      "en-us"
                    )}`}</p>
                  </div>
                </StyledRevenueListItem>
              </Col>
            </Fragment>
          ))}
        </Row>
      </Drawer>
    </div>
  );
}
